import { useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { usePessoas, usePessoa } from '../hooks/usePessoa'
import { PessoaFilters, Pessoa, PessoaFormData } from '../tipos/pessoa'
import { PessoaFiltersComponent } from '../componentes/FiltrosPessoa'
import { PessoaList } from '../componentes/ListaPessoa'
import { FormularioPessoa } from '../componentes/FormularioPessoa'

const convertPessoaToFormData = (pessoa: Pessoa | null): PessoaFormData | undefined => {
  if (!pessoa) return undefined

  return {
    idPessoa: pessoa.idPessoa,
    nome: pessoa.nome,
    email: pessoa.email,
    telefone: pessoa.telefone,
    observacao: pessoa.observacao,
    cargoCodigo: pessoa.cargoCodigo ? String(pessoa.cargoCodigo) : '',
    ativo: pessoa.ativo,
    timeId: pessoa.timeId != null ? String(pessoa.timeId) : '',
  }
}

function Pessoas() {
  const [filters, setFilters] = useState<PessoaFilters>({
    search: '',
    ativo: 'todos',
    cargoCodigo: 'todos',
  })
  const [showForm, setShowForm] = useState(false)
  const [editingPessoa, setEditingPessoa] = useState<Pessoa | null>(null)
  const [editingPessoaId, setEditingPessoaId] = useState<number | null>(null)

  const { data: pessoas = [], isLoading, refetch } = usePessoas(filters)
  const {
    data: pessoaCompleta,
    isLoading: isLoadingPessoa,
  } = usePessoa(editingPessoaId ?? undefined)

  const pessoaParaEdicao = useMemo(() => {
    if (pessoaCompleta) return pessoaCompleta
    return convertPessoaToFormData(editingPessoa)
  }, [pessoaCompleta, editingPessoa])

  const handleSearch = (newFilters: PessoaFilters) => {
    setFilters(newFilters)
  }

  const handleCreatePessoa = () => {
    setEditingPessoa(null)
    setEditingPessoaId(null)
    setShowForm(true)
  }

  const handleEditPessoa = (pessoa: Pessoa) => {
    setEditingPessoa(pessoa)
    setEditingPessoaId(pessoa.idPessoa || null)
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingPessoa(null)
    setEditingPessoaId(null)
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pessoas</h1>
          <p className="mt-1 text-sm text-gray-600">Gerencie a equipe e colaboradores</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={handleCreatePessoa}
            className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 shadow-theme-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-2 inline" />
            Nova Pessoa
          </button>
        </div>
      </div>

      <PessoaFiltersComponent onSearch={handleSearch} />

      <PessoaList pessoas={pessoas} isLoading={isLoading} onRefresh={refetch} onEditPessoa={handleEditPessoa} />

      <FormularioPessoa
        isOpen={showForm}
        onClose={handleCloseForm}
        pessoa={pessoaParaEdicao}
        isEditing={!!editingPessoaId}
      />

      {isLoadingPessoa && editingPessoaId && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
            <div className="loading-spinner w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-700">Carregando dados da pessoa...</span>
          </div>
        </div>
      )}
    </>
  )
}

export default Pessoas
